import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { isUSRegionSelector, useRegionStore } from '@packages/stores';

import { Text } from '@elements/text';
import { TextWithMd } from '@elements/text/textWithMd';
import { sluggify, testProps } from '@utils';

import { Item, iconMap } from './needAssistance.types';

export const NeedAssistance = () => {
  const isUS = useRegionStore(isUSRegionSelector);
  const { t } = useTranslation();
  const items = t('confirmation.needAssistance.items', {
    returnObjects: true,
  }) as unknown as Item[];

  return (
    <div className="flex flex-col gap-s24">
      <Text {...testProps('confirmation-need-assistance-title')} variant="h4-24/sb">
        {t('confirmation.needAssistance.title')}
      </Text>
      <Text {...testProps('confirmation-need-assistance-subtitle')} variant="subTitle-20/sb">
        {t('confirmation.needAssistance.subtitle')}
      </Text>
      <div>
        <TextWithMd {...testProps('confirmation-need-assistance-operating-hours')}>
          {t('confirmation.needAssistance.operatingHours')}
        </TextWithMd>
        {!isUS ? (
          <TextWithMd {...testProps('confirmation-need-assistance-emergency')}>
            {t('confirmation.needAssistance.emergency')}
          </TextWithMd>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-s8 md:grid-cols-2">
        {items.map((it, index) => {
          return (
            <InfoCard
              className="flex flex-1"
              icon={it.icon}
              title={it.title}
              description={it.description}
              key={index}
            />
          );
        })}
      </div>
      <USClaimsSupport />
    </div>
  );
};

const USClaimsSupport = () => {
  const { t } = useTranslation();
  const claimItems = t('confirmation.needAssistance.claimsItems', {
    returnObjects: true,
  }) as unknown as Item[];
  const isUS = useRegionStore(isUSRegionSelector);

  if (!isUS || !claimItems?.length) {
    return null;
  }

  return (
    <div className="flex flex-col gap-s16 ">
      <Text {...testProps('confirmation-need-assistance-claims-title')} variant="subTitle-20/sb">
        {t('confirmation.needAssistance.claimsTitle')}
      </Text>
      <Text {...testProps('confirmation-need-assistance-claims-operating-hours')} className="">
        {t('confirmation.needAssistance.claimOperatingHours')}
      </Text>
      <div className="grid grid-cols-1 gap-s8 md:grid-cols-2">
        {claimItems.map((it, index) => {
          return (
            <InfoCard
              className="flex flex-1"
              icon={it.icon}
              title={it.title}
              description={it.description}
              key={index}
            />
          );
        })}
      </div>
    </div>
  );
};

type Props = Item & {
  className?: string;
};

const InfoCard = ({ icon, title = 'chat', description = 'chat', className }: Props) => {
  const Icon = iconMap?.[icon];
  if (!Icon) {
    return null;
  }

  return (
    <div className={twMerge('flex flex-col gap-s8 bg-cabo-50 rounded-2xl p-s16', className)}>
      <div className="flex p-s8 bg-surface-primary rounded-[32px] w-12 h-12 justify-center items-center">
        <Icon height={32} width={32} />
      </div>
      <Text {...testProps(`confirmation-need-assistance-${sluggify(title)}`)} variant="body-16/sb">
        {title}
      </Text>
      <InfoCardText description={description} />
    </div>
  );
};

const InfoCardText = ({ description }: { description: string }) => {
  if (description.includes('tel')) {
    const [text, href] = extractDisplayText(description);
    return (
      <a href={href}>
        <Text
          {...testProps(`confirmation-need-assistance-${sluggify(text)}`)}
          variant="subHeading-14/r">
          {text}
        </Text>
      </a>
    );
  }

  return (
    <TextWithMd
      {...testProps(`confirmation-need-assistance-${sluggify(description)}`)}
      variant="subHeading-14/r">
      {description}
    </TextWithMd>
  );
};

function extractDisplayText(input: string): string[] {
  const regex = /\[([^\]]+)\]\((tel:[^)]+)\)/g;
  const result: string[] = [];
  let match;

  while ((match = regex.exec(input)) !== null) {
    result.push(match[1]); // display text
    result.push(match[2]); // tel: link
  }

  return result;
}
